import { useState, useEffect, useRef, useCallback } from "react";
import CreatePost from "./CreatePost";
import { ListComment } from "./ListComment";
import { projectURL } from "./utils/mockdata";
import LikeSound from "./audio/Like.wav";
import UnLikeSound from "./audio/UnLike.wav";
import 'react-quill/dist/quill.snow.css'; // or 'bubble.css' for bubble theme
import ReactQuill from 'react-quill';
import DOMPurify from 'dompurify';

// Loader shimmer
const PostShimmer = () => (
  <div className="flex flex-col space-y-6 w-full max-w-4xl mx-auto">
    {[...Array(5)].map((_, idx) => (
      <div
        key={idx}
        className="flex justify-center items-center w-full p-4 border border-slate-300 rounded-lg shadow-sm bg-white animate-pulse"
      >
        <div className="flex flex-col w-full space-y-4">
          <div className="flex items-center space-x-3">
            <div className="rounded-full bg-gray-300 h-10 w-10"></div>
            <div className="flex flex-col space-y-1">
              <div className="h-4 bg-gray-300 rounded w-24"></div>
              <div className="h-3 bg-gray-300 rounded w-16"></div>
            </div>
          </div>
          <hr />
          <div className="h-10 bg-gray-300 rounded w-6/6"></div>
          <div className="h-6 bg-gray-300 rounded w-[30px]"></div>
          <div className="h-10 bg-gray-300 rounded w-6/6"></div>
        </div>
      </div>
    ))}
  </div>
);

const Listpost = () => {
  const [userPost, setUserPost] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshPosts, setRefreshPosts] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editPostId, setEditPostId] = useState(null);
  const [editContent, setEditContent] = useState("");

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletePostId, setDeletePostId] = useState(null);

  const likeaudioRef = useRef(null);
  const unlikeaudioRef = useRef(null);
  const loaderRef = useRef(null);


  const loggedUserId = sessionStorage.getItem("loggedUserId");

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = () => setDropdownOpen(null);
    window.addEventListener("click", handleClickOutside);
    return () => window.removeEventListener("click", handleClickOutside);
  }, []);

  const fetchPosts = useCallback(async (page = 1) => {
    setIsLoading(true);

    try {
      const postList = await fetch(`${projectURL}posts?page=${page}&user_id=${loggedUserId}`, {
        headers: { "Content-Type": "application/json" },
      });
      const postJson = await postList.json();
      const newPosts = postJson?.posts || [];

      setUserPost((prev) => {
        if (page === 1) return newPosts;
        const existingIds = new Set(prev.map((post) => post._id));
        const filtered = newPosts.filter((p) => !existingIds.has(p._id));
        return [...prev, ...filtered];
      });

      setHasMorePosts(newPosts.length > 0);
    } catch (err) {
      console.error("Error fetching posts:", err);
    } finally {
      setIsLoading(false);
    }
  }, [loggedUserId]);

  const handleLike = (postId, createdBy) => {
    const userId = sessionStorage.getItem("loggedUserId");
    const userName = sessionStorage.getItem("loggedUserName");

    fetch(`${projectURL}likes/create`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ post_id: postId, user_id: userId }),
    })
      .then((res) => res.json())
      .then((data) => {
        setUserPost((prevPosts) =>
          prevPosts.map((post) => {
            if (post._id === postId) {
              const updatedLikes =
                data?.message === "Like added"
                  ? (post.likes || 0) + 1
                  : Math.max((post.likes || 1) - 1, 0);
              return {
                ...post,
                likes: updatedLikes,
                isLiked: data?.message === "Like added",
              };
            }
            return post;
          })
        );

        if (data?.message === "Like added") {
          likeaudioRef.current.play();

          /*
          * CREATE LIKE NOTIFICATION
          * {message,from,to}
          * 
          */
          const message = userName + ' Like your Post';
          const dataNotification = {
            postId,
            message,
            from: userId,
            to: createdBy
          };
          // dataNotification = JSON.stringify({ dataNotification });
          // console.log('dataNotification', dataNotification);
          fetch(`${projectURL}notification/create`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(dataNotification),
          })
            .then((res) =>
              res.json())
            .then((data) => {
              console.log('Notification Data', data);
            });

        } else {
          unlikeaudioRef.current.play();
        }
      })
      .catch((err) => console.error("Like error:", err));
  };

  const handleEdit = (postId) => {
    const post = userPost.find((p) => p._id === postId);
    setEditPostId(postId);
    setEditContent(post?.post || "");
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const res = await fetch(`${projectURL}posts/${editPostId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ post: editContent }),
      });

      if (res.ok) {
        const updatedTime = new Date().toISOString();

        setUserPost((prev) =>
          prev.map((p) =>
            p._id === editPostId ? { ...p, post: editContent, updated_at: updatedTime } : p
          )
        );
        setIsEditModalOpen(false);
        setEditPostId(null);
        setEditContent("");
      }
    } catch (err) {
      console.error("Update error:", err);
    }
  };

  const handleDeleteClick = (postId) => {
    setDeletePostId(postId);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      const res = await fetch(`${projectURL}posts/${deletePostId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });

      if (res.ok) {
        setUserPost((prev) => prev.filter((p) => p._id !== deletePostId));
        setIsDeleteModalOpen(false);
        setDeletePostId(null);
      }
    } catch (err) {
      // console.error("Delete error:", err);
    }
  };

  const timeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const seconds = Math.floor((now - date) / 1000);
    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60,
      second: 1,
    };
    for (const [unit, value] of Object.entries(intervals)) {
      const count = Math.floor(seconds / value);
      if (count >= 1) return `${count} ${unit}${count > 1 ? "s" : ""} ago`;
    }
    return "just now";
  };

  useEffect(() => {
    likeaudioRef.current = new Audio(LikeSound);
    unlikeaudioRef.current = new Audio(UnLikeSound);
  }, []);

  useEffect(() => {
    fetchPosts(currentPage);
  }, [currentPage, fetchPosts]);

  useEffect(() => {
    if (refreshPosts) {
      setCurrentPage(1);
      fetchPosts(1);
      setRefreshPosts(false);
    }
  }, [refreshPosts, fetchPosts]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMorePosts && !isLoading) {
          setCurrentPage((prev) => prev + 1);
        }
      },
      { threshold: 1.0 }
    );

    const currentLoader = loaderRef.current;
    if (currentLoader) observer.observe(currentLoader);

    return () => {
      if (currentLoader) observer.unobserve(currentLoader);
    };
  }, [hasMorePosts, isLoading]);

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      // [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'list': 'check' }],
      ['link', 'image', 'video'],
      ['blockquote'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'font': [] }],
      [{ 'align': [] }],
      ['clean']
    ],
  };

  const formats = [
    'header', 'size',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'link', 'image', 'video', 'blockquote', 'size', 'color', 'background', 'font', 'align'
  ];


  return (
    <>
      <CreatePost onPostCreated={() => setRefreshPosts(true)} />

      {userPost.map((data) => (
        <div className="flex justify-center items-center" key={data?._id}>
          <div className="relative flex flex-col my-6 bg-white shadow-sm border border-slate-500 rounded-lg w-full max-w-4xl">
            <div className="flex items-center justify-between p-2">
              <div className="flex items-center">
                <img
                  alt="User Avatar"
                  src={data?.created_by?.imagePath}
                  className="h-14 w-14 rounded-full border border-green-900"
                />
                <div className="flex flex-col ml-4 text-md">
                  <span className="font-extrabold text-blue-500">{data?.created_by?.username}</span>
                  <span className="text-slate-400"> {timeAgo(data?.created_at)}
                    {data?.created_at === data?.updated_at ? "" : " | 🖊️edited"}
                  </span>
                </div>
              </div>

              {/* Dropdown */}
              {data?.created_by?._id === loggedUserId && (
                <div className="relative" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() =>
                      setDropdownOpen((prev) => (prev === data._id ? null : data._id))
                    }
                    className="text-gray-600 hover:text-gray-800 px-2 py-1 rounded-full"
                  >
                    ⋮
                  </button>
                  {dropdownOpen === data._id && (
                    <div className="absolute right-2 w-28 bg-white border border-gray-300 rounded shadow-md z-10">
                      <button
                        onClick={() => handleEdit(data._id)}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteClick(data._id)}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 text-red-500"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <hr />
            <div className="p-4">
              <div className="prose max-w-full text-slate-800 text-base" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data?.post) }}></div>
              <div className="text-slate-600 leading-normal font-light">

                <div className="flex">
                  {(data?.isLiked === true) ?
                    <button
                      className="mt-2 mb-2 flex px-2.5 hover:scale-110 hover:shadow text-white bg-gradient-to-br from-purple-600 to-blue-500 font-medium rounded-xl text-md h-6 items-center gap-2"
                      onClick={() => handleLike(data._id, data.created_by._id)}
                    >
                      <svg className="w-4 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="white" strokeOpacity={0.9} strokeWidth="2">
                        <path strokeLinecap="square" strokeLinejoin="square" d="M6.633 10.5c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75A2.25 2.25 0 0116.5 4.5c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 01-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 00-1.423-.23H5.904M14.25 9h2.25M5.904 18.75c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 01-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 10.203 4.167 9.75 5 9.75h1.053c.472 0 .745.556.5.96a8.958 8.958 0 00-1.302 4.665c0 1.194.232 2.333.654 3.375z" />
                      </svg>
                      <span>{data.likes}</span>
                    </button> :
                    <button
                      className="mt-2 mb-2 flex px-2.5 hover:scale-110 hover:shadow text-black bg-gradient-to-br from-white-200 to-white-500 font-medium rounded-xl text-md h-6 items-center gap-2 border border-gray-600"
                      onClick={() => handleLike(data._id, data.created_by._id)}
                    >
                      <svg className="w-4 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="black" strokeOpacity={0.9} strokeWidth="2">
                        <path strokeLinecap="square" strokeLinejoin="square" d="M6.633 10.5c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75A2.25 2.25 0 0116.5 4.5c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 01-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 00-1.423-.23H5.904M14.25 9h2.25M5.904 18.75c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 01-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 10.203 4.167 9.75 5 9.75h1.053c.472 0 .745.556.5.96a8.958 8.958 0 00-1.302 4.665c0 1.194.232 2.333.654 3.375z" />
                      </svg>
                      <span>{data.likes}</span>
                    </button>
                  }

                  {/* <span className="mt-4 mb-4 px-28 text-blue-900 ">
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-message-square-text-icon lucide-message-square-text"><path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z"/><path d="M7 11h10"/><path d="M7 15h6"/><path d="M7 7h8"/></svg>
                  </span> */}
                </div>

                <ListComment postId={data._id} />
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-4 rounded-lg ">
            <h3 className="text-lg font-semibold mb-2">Edit Post</h3>
            <ReactQuill
              theme="snow"
              modules={modules}
              formats={formats}
              value={editContent}
              onChange={setEditContent}
              rows={8}
              className="bg-white rounded-lg border border-gray-300 "
            />
            <div className="flex justify-end mt-2 gap-2">
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 text-sm"
              >Cancel</button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
              >Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-4 rounded-lg w-full max-w-md">
            <h3 className="text-lg font-semibold mb-2">Are you sure you want to delete this post?</h3>
            <div className="flex justify-end mt-2 gap-2">
              <button
                onClick={() => setIsDeleteModalOpen(false)} // Close modal without deleting
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 text-sm"
              >Cancel</button>
              <button
                onClick={handleDelete} // Proceed with deletion
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
              >Delete</button>
            </div>
          </div>
        </div>
      )}

      <div ref={loaderRef} className="flex justify-center py-1">
        {isLoading && <PostShimmer />}
      </div>
    </>
  );
};

export default Listpost;
